import { FC, useEffect, useState } from 'react';
import {
  BoxV2 as Box,
  MultimediaList,
  MultimediaUploader,
  AttachmentPreview,
  RoleGuard,
  ButtonV2 as Button,
  Grid,
  Flex,
} from 'portal-commons';

import DeleteSampleMultimediaModal from './DeleteSampleMultimediaModal';
import UploadSizeExceededModal from './UploadSizeExceededModal';
import { Attachment, FileFormats } from '../types';
import { MAX_FILE_SIZE } from '../constants';
import { attachmentController, AttachmentFolder } from '@/apis/core';
import { generateAndDownloadFile } from '@/utils/download';

interface AttachmentUploaderProps {
  readonly?: boolean;
  campaignUid: string;
  folder: AttachmentFolder;
  maxCount: number;
  disabledText?: string;
}

const AttachmentUploader: FC<AttachmentUploaderProps> = ({
  readonly = false,
  campaignUid,
  folder,
  maxCount,
  disabledText,
}) => {
  const [supportedFileFormats, setSupportedFileFormats] = useState<FileFormats>(
    {}
  );
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [upload, setUpload] = useState<File | undefined>();
  const [previewIndex, setPreviewIndex] = useState(-1);
  const [files, setFiles] = useState<Record<string, File>>({});
  const previewUuid = attachments[previewIndex]?.uuid;
  const [deleteIndex, setDeleteIndex] = useState(-1);
  const [sizeExceededModalOpen, setSizeExceededModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      if (!folder) return;
      const fileFormats = await attachmentController.getSupportedFileFormats(
        folder
      );
      if (fileFormats) {
        setSupportedFileFormats(fileFormats);
      }
    })();
    (async () => {
      if (!folder || !campaignUid) return;
      const docs = await attachmentController.getAttachmentsForCampaign(
        campaignUid,
        folder
      );
      if (docs) {
        setAttachments(docs);
      }
    })();
  }, [campaignUid, folder]);

  const handleChange = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      setUpload(undefined);
      setSizeExceededModalOpen(true);
    } else {
      setUpload(file);
    }
  };

  const handleAdd = async () => {
    if (upload && attachments.length < maxCount) {
      setLoading(true);
      const attachment = await attachmentController.uploadAttachmentForCampaign(
        campaignUid,
        folder,
        upload
      );
      if (attachment) {
        const newFiles = { ...files };
        newFiles[attachment.uuid] = upload;
        setFiles(newFiles);
        setAttachments([...attachments, attachment]);
        setUpload(undefined);
      }
      setLoading(false);
    }
  };

  const handleDownload = async (index: number) => {
    const uuid = attachments[index]?.uuid;
    const fileName = attachments[index]?.fileName;
    if (!uuid) return;

    let targetFile = files[uuid];

    if (!targetFile) {
      const downloadedFile = await attachmentController.downloadAttachment({
        uuid,
      });
      if (downloadedFile) {
        targetFile = downloadedFile;
        setFiles((prev) => ({ ...prev, [uuid]: downloadedFile }));
      }
    }

    if (targetFile) {
      generateAndDownloadFile(targetFile, fileName);
    }
  };

  const handleSelect = async (index: number) => {
    setPreviewIndex(index);
    const uuid = attachments[index]?.uuid;
    if (uuid && !files[uuid]) {
      const file = await attachmentController.downloadAttachment({
        uuid,
      });
      if (file) {
        const newFiles = { ...files };
        newFiles[uuid] = file;
        setFiles(newFiles);
      }
    }
  };

  const handleDelete = (index: number) => {
    setDeleteIndex(index);
  };

  const applyDelete = async () => {
    if (attachments.length > deleteIndex) {
      const newAttachments = [...attachments];
      newAttachments.splice(deleteIndex, 1);
      setAttachments(newAttachments);
      setDeleteIndex(-1);

      const uuid = await attachmentController.deleteAttachment(
        attachments[deleteIndex].uuid
      );
      if (!uuid) {
        // Revert attachments if API call fails
        setAttachments(attachments);
      }
    }
  };

  return (
    <Box sx={{ width: '100%' }} data-testid="attachmentUploader">
      <Grid
        sx={{
          gridTemplateColumns: readonly ? '1fr' : '1fr 1fr',
          gap: '32px',
        }}
      >
        {!readonly && (
          <RoleGuard feature="campaignDetail.addCtaMultimedia">
            <Box>
              <MultimediaUploader
                disabled={attachments.length >= maxCount}
                disabledText={
                  disabledText ??
                  `Max ${maxCount} uploads per campaign is allowed`
                }
                supportedMimeTypes={supportedFileFormats}
                onChange={handleChange}
              />
            </Box>
          </RoleGuard>
        )}
        <Box style={{ minHeight: '307px' }}>
          <RoleGuard
            feature="campaignDetail.deleteCtaMultimedia"
            render={(authorized) => (
              <MultimediaList
                editable={!readonly && authorized}
                title="Multimedia Files"
                attachments={attachments}
                loading={previewIndex > -1 && !files[previewUuid]}
                onDownload={handleDownload}
                onSelect={handleSelect}
                onDelete={handleDelete}
              />
            )}
          />
          <DeleteSampleMultimediaModal
            open={!!attachments[deleteIndex]}
            fileName={attachments[deleteIndex]?.fileName}
            onCancel={() => setDeleteIndex(-1)}
            onSubmit={applyDelete}
          />
        </Box>
        {attachments.length < maxCount && !readonly && (
          <Flex sx={{ justifyContent: 'center', gridColumn: '1 / -1' }}>
            <Button
              type="button"
              data-testid="attachmentUploaderAddButton"
              disabled={loading || !upload}
              variant="fill"
              shape="square"
              size="sm"
              color="primary"
              onClick={handleAdd}
            >
              {loading ? 'Processing...' : '+ Add Sample Multimedia'}
            </Button>
          </Flex>
        )}
        {!!files[previewUuid] && (
          <AttachmentPreview
            file={files[previewUuid]}
            mimeType={attachments[previewIndex].mimeType}
            onClose={() => setPreviewIndex(-1)}
            data-testid="attachmentUploaderPreviewBackdrop"
          />
        )}
      </Grid>
      <UploadSizeExceededModal
        open={sizeExceededModalOpen}
        onClose={() => setSizeExceededModalOpen(false)}
      />
    </Box>
  );
};

export default AttachmentUploader;
